import { createContext, useContext } from "react";
import type { Breakpoint } from "@mui/material/styles";
import type { To } from "react-router";

/** How far below the top of the window drawers start, in px: one number or one per breakpoint. */
export type DetailStackTopOffset = number | Partial<Record<Breakpoint, number>>;

/** A pending close: every drawer deeper than `depth` slides out, then the URL becomes `to`. -1 closes the whole stack. */
export interface DetailStackCollapse {
  depth: number;
  to: To;
}

/** One level of the stack, as the drawer at that level (and anything inside it) sees it. */
export interface DetailStackLevel<K extends string = string> {
  kind: K;
  /** null for a kind that takes no id. */
  id: string | null;
  /** 0 for the first drawer over the page, +1 per level. */
  depth: number;
  /** How many drawers are open. */
  size: number;
  /** The drawer in front, the one Esc / × close. */
  isTop: boolean;
  /** The first drawer over the page, the only one whose backdrop catches clicks. */
  isBottom: boolean;
  width: string;
  /** How far left of the window's edge it sits: 0 on top, more for each level above it. */
  offsetPx: number;
  /** The URL without this level (and everything above). */
  closeTo: To;
  /** The URL with this level on top. */
  selfTo: To;
  /** A link opening `kind` on top of this level, dropping anything above. Throws when the id doesn't match the kind. */
  toChild: (kind: K, id?: string) => To;
  /** A link with this level swapped for `kind` (e.g. a finished form for the record it made). Throws when the id doesn't match the kind. */
  toSwap: (kind: K, id?: string) => To;
  /** Slides this level (and everything above) out, then goes to closeTo. */
  close: () => void;
  /** Makes this level the top: slides out everything above it, then goes to selfTo. */
  focus: () => void;
}

/** State shared by every drawer of one stack. */
export interface DetailStackShared {
  collapse: DetailStackCollapse | null;
  topOffset: DetailStackTopOffset;
}

/** The stack's shared state, provided by DetailStack. */
export const DetailStackSharedContext = createContext<DetailStackShared | null>(null);

/** The level a component is rendered in, provided by DetailStack per drawer; read with useDetailStackLevel. */
export const DetailStackLevelContext = createContext<DetailStackLevel | null>(null);

/** The detail-stack level this component is rendered in, or null outside a stack. `K` is the app's kind union. */
export const useDetailStackLevel = <K extends string = string>(): DetailStackLevel<K> | null => {
  const level = useContext(DetailStackLevelContext);
  return level as DetailStackLevel<K> | null;
};

/** useDetailStackLevel for a drawer component itself: throws outside a stack instead of returning null. */
export const useDetailStackDrawer = <K extends string = string>(): DetailStackLevel<K> => {
  const level = useDetailStackLevel<K>();
  if (!level) {
    throw new Error("useDetailStackDrawer must be used inside a DetailStack drawer");
  }
  return level;
};

/** The id of the drawer's record, for a kind that takes one; throws for an id-less kind. */
export const useDetailStackId = (): string => {
  const level = useDetailStackDrawer();
  if (level.id === null) {
    throw new Error(`useDetailStackId: "${level.kind}" drawers take no id`);
  }
  return level.id;
};
